import {
  GoogleAuthProvider,
  linkWithPopup,
  signInWithCredential,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { auth } from "../../configs/firebaseConfig";
import {
  getSafeAuthFailureProperties,
  getAnalyticsUserState,
  identifySignedInUser,
  resetAnalytics,
  trackMixpanelEvent,
} from "../Analytics";
import {
  waitForAuthInitialization,
  waitForSettledAuthUser,
} from "./authState";

export const GOOGLE_AUTH_OUTCOMES = Object.freeze({
  SUCCESS: "success",
  CANCELLED: "cancelled",
  CONFLICT: "conflict",
  MISSING_ANONYMOUS_USER: "missing-anonymous-user",
  NON_ANONYMOUS_USER: "non-anonymous-user",
  UID_MISMATCH: "uid-mismatch",
  FAILURE: "failure",
});

const POPUP_CANCELLATION_ERROR_CODES = new Set([
  "auth/popup-closed-by-user",
  "auth/cancelled-popup-request",
  "auth/popup-blocked",
]);

const AUTH_CONFLICT_ERROR_CODES = new Set([
  "auth/account-exists-with-different-credential",
  "auth/credential-already-in-use",
  "auth/email-already-in-use",
  "auth/provider-already-linked",
]);

async function refreshAuthToken(user) {
  if (typeof user?.getIdToken === "function") {
    try {
      await user.getIdToken(true);
      return true;
    } catch {
      return false;
    }
  }

  return true;
}

function getErrorCode(error) {
  return error?.code || null;
}

function getErrorEmail(error) {
  const email = error?.customData?.email || error?.email;

  return typeof email === "string" && email.trim()
    ? email.trim()
    : null;
}

function getGoogleCredentialFromError(error) {
  try {
    return (
      GoogleAuthProvider.credentialFromError(error) ||
      error?.credential ||
      null
    );
  } catch {
    return error?.credential || null;
  }
}

function classifyGoogleAuthError(error, authStep = "popup") {
  const errorCode = getErrorCode(error);

  if (POPUP_CANCELLATION_ERROR_CODES.has(errorCode)) {
    return {
      status: GOOGLE_AUTH_OUTCOMES.CANCELLED,
      errorCode,
    };
  }

  if (AUTH_CONFLICT_ERROR_CODES.has(errorCode)) {
    trackMixpanelEvent(
      "Authentication Failed",
      getSafeAuthFailureProperties({
        authProvider: "google",
        authStep,
        errorCode,
        userState: getAnalyticsUserState(auth.currentUser),
      })
    );
    const conflictResult = {
      status: GOOGLE_AUTH_OUTCOMES.CONFLICT,
      errorCode,
    };
    const targetEmail = getErrorEmail(error);
    const credential = getGoogleCredentialFromError(error);

    if (targetEmail) {
      conflictResult.targetEmail = targetEmail;
    }

    if (credential) {
      conflictResult.credential = credential;
    }

    return conflictResult;
  }

  trackMixpanelEvent(
    "Authentication Failed",
    getSafeAuthFailureProperties({
      authProvider: "google",
      authStep,
      errorCode,
      userState: getAnalyticsUserState(auth.currentUser),
    })
  );

  return {
    status: GOOGLE_AUTH_OUTCOMES.FAILURE,
    errorCode,
  };
}

function createGoogleProvider() {
  const provider = new GoogleAuthProvider();

  provider.setCustomParameters({
    prompt: "select_account",
  });

  return provider;
}

let activePopupRequest = null;

function runGooglePopupRequest(request) {
  if (activePopupRequest) {
    return Promise.resolve({
      status: GOOGLE_AUTH_OUTCOMES.CANCELLED,
      errorCode: "auth/cancelled-popup-request",
    });
  }

  activePopupRequest = Promise.resolve()
    .then(request)
    .finally(() => {
      activePopupRequest = null;
    });

  return activePopupRequest;
}

export async function linkAnonymousUserWithGoogle() {
  await waitForAuthInitialization();

  const currentUser = auth.currentUser;

  if (!currentUser) {
    return {
      status: GOOGLE_AUTH_OUTCOMES.MISSING_ANONYMOUS_USER,
    };
  }

  if (currentUser.isAnonymous !== true) {
    return {
      status: GOOGLE_AUTH_OUTCOMES.NON_ANONYMOUS_USER,
      uid: currentUser.uid,
    };
  }

  const uidBeforeLink = currentUser.uid;

  try {
    const userCredential = await runGooglePopupRequest(() =>
      linkWithPopup(currentUser, createGoogleProvider())
    );

    if (userCredential?.status) return userCredential;
    const linkedUid = userCredential?.user?.uid;

    if (linkedUid !== uidBeforeLink) {
      return {
        status: GOOGLE_AUTH_OUTCOMES.UID_MISMATCH,
        expectedUid: uidBeforeLink,
        uid: linkedUid || null,
        uidPreserved: false,
      };
    }

    if (!(await refreshAuthToken(userCredential.user))) {
      return {
        status: GOOGLE_AUTH_OUTCOMES.FAILURE,
        errorCode: "auth/token-refresh-failed",
      };
    }
    const settledUser = await waitForSettledAuthUser({
      contextUser: userCredential.user,
      expectedUid: uidBeforeLink,
      requirePermanentUser: true,
      skipInitialization: true,
    });

    if (!settledUser || settledUser.uid !== uidBeforeLink) {
      return {
        status: GOOGLE_AUTH_OUTCOMES.UID_MISMATCH,
        expectedUid: uidBeforeLink,
        uid: settledUser?.uid || null,
        uidPreserved: false,
      };
    }
    identifySignedInUser(uidBeforeLink);
    trackMixpanelEvent("User Signed In", {
      auth_provider: "google",
      auth_flow: "link",
    });

    return {
      status: GOOGLE_AUTH_OUTCOMES.SUCCESS,
      uid: uidBeforeLink,
      uidPreserved: true,
    };
  } catch (error) {
    return classifyGoogleAuthError(error, "linking");
  }
}

export async function signInWithGoogle() {
  await waitForAuthInitialization();

  try {
    const userCredential = await runGooglePopupRequest(() =>
      signInWithPopup(auth, createGoogleProvider())
    );

    if (userCredential?.status) return userCredential;

    if (!(await refreshAuthToken(userCredential.user))) {
      return {
        status: GOOGLE_AUTH_OUTCOMES.FAILURE,
        errorCode: "auth/token-refresh-failed",
      };
    }
    const settledUser = await waitForSettledAuthUser({
      contextUser: userCredential.user,
      expectedUid: userCredential.user.uid,
      requirePermanentUser: true,
      skipInitialization: true,
    });

    if (!settledUser || settledUser.uid !== userCredential.user.uid) {
      return {
        status: GOOGLE_AUTH_OUTCOMES.FAILURE,
        errorCode: "auth/user-state-not-settled",
      };
    }

    identifySignedInUser(settledUser.uid);
    trackMixpanelEvent("User Signed In", {
      auth_provider: "google",
      auth_flow: "sign_in",
    });

    return {
      status: GOOGLE_AUTH_OUTCOMES.SUCCESS,
      uid: userCredential.user.uid,
    };
  } catch (error) {
    return classifyGoogleAuthError(error, "popup");
  }
}

export async function signInWithGoogleCredential(credential) {
  if (!credential) {
    return {
      status: GOOGLE_AUTH_OUTCOMES.FAILURE,
      errorCode: "auth/missing-google-credential",
    };
  }

  try {
    const userCredential = await signInWithCredential(auth, credential);

    if (!(await refreshAuthToken(userCredential.user))) {
      return {
        status: GOOGLE_AUTH_OUTCOMES.FAILURE,
        errorCode: "auth/token-refresh-failed",
      };
    }
    const settledUser = await waitForSettledAuthUser({
      contextUser: userCredential.user,
      expectedUid: userCredential.user.uid,
      requirePermanentUser: true,
      skipInitialization: true,
    });

    if (!settledUser || settledUser.uid !== userCredential.user.uid) {
      return {
        status: GOOGLE_AUTH_OUTCOMES.FAILURE,
        errorCode: "auth/user-state-not-settled",
      };
    }

    identifySignedInUser(settledUser.uid);
    trackMixpanelEvent("User Signed In", {
      auth_provider: "google",
      auth_flow: "sign_in",
    });

    return {
      status: GOOGLE_AUTH_OUTCOMES.SUCCESS,
      uid: userCredential.user.uid,
    };
  } catch (error) {
    return classifyGoogleAuthError(error, "redirect");
  }
}

export async function signOutUser() {
  try {
    await signOut(auth);
    resetAnalytics();

    return {
      status: GOOGLE_AUTH_OUTCOMES.SUCCESS,
    };
  } catch (error) {
    return {
      status: GOOGLE_AUTH_OUTCOMES.FAILURE,
      errorCode: getErrorCode(error),
    };
  }
}
